import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card } from 'src/shared/components/ui/card'
import { cn } from 'src/shared/components/ui/utils'
import { RolePageData } from '../../types'

interface RoleFAQSectionProps {
  data: RolePageData
}

interface FAQItemProps {
  question: string
  answer: string
  isOpen: boolean
  onToggle: () => void
}

const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => {
  return (
    <button
      onClick={onToggle}
      className="w-full text-left py-5 border-b border-white/60 last:border-b-0"
    >
      <Row className="items-center justify-between gap-4">
        <p className="font-medium text-slate-800">{question}</p>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-slate-400 transition-transform flex-shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </Row>
      {isOpen && (
        <p className="mt-3 text-slate-500 text-sm leading-relaxed pr-8">
          {answer}
        </p>
      )}
    </button>
  )
}

export const RoleFAQSection = ({ data }: RoleFAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="w-full py-24 bg-gradient-to-b from-white via-stone-50 to-stone-100">
      <Column className="w-full max-w-3xl mx-auto px-8 gap-12">
        {/* Header */}
        <Column className="gap-4 text-center">
          <h2 className="text-3xl font-normal text-slate-800">
            Questions from {data.label.toLowerCase()}
          </h2>
          <p className="text-slate-500">
            Common questions we hear from people in your role.
          </p>
        </Column>

        {/* FAQ list */}
        <Card className="p-6 bg-white/70 backdrop-blur-sm border border-white/60 rounded-2xl">
          <Column>
            {data.faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => toggleFAQ(index)}
              />
            ))}
          </Column>
        </Card>
      </Column>
    </section>
  )
}
